/* eslint-disable no-useless-escape */
import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, PushNotificationService, TenantService, UserService, WebSocketService, environment, SoundService } from '@org/shared-services';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SwUpdate } from '@angular/service-worker';
import { Observable, take, timer } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { SoundPermissionModalComponent } from '../../../../shared-services/src/lib/notification/sound-permission-modal/sound-permission-modal.component';

@Component({
    imports: [FormsModule, CommonModule, MatTooltipModule],
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-login',
    templateUrl: './login.component.html',
    animations: [
      trigger('fadeInOut', [
        transition(':enter', [
          style({ opacity: 0 }),
          animate('160ms cubic-bezier(0.23, 1, 0.32, 1)', style({ opacity: 1 }))
        ]),
        transition(':leave', [
          animate('120ms cubic-bezier(0.23, 1, 0.32, 1)', style({ opacity: 0 }))
        ])
      ])
    ]
})
export class LoginComponent {
  email = '';
  password = '';
  showPassword = false;
  version = environment.version;
  userAssignedTo$!: Observable<boolean>;
  emailInvalid: boolean = false;
  passwordInvalid: boolean = false;
  credentialsInvalid = false;
  loading = false;
  
  constructor(
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
    private webSocketService: WebSocketService,
    private tenantService: TenantService,
    private pushNotificationService: PushNotificationService,
    private soundService: SoundService,
    private dialog: MatDialog
    ) {
      this.userAssignedTo$ = this.tenantService.userAssignedTo$;
    }

  login() {
    this.emailInvalid = !this.email;
    this.passwordInvalid = !this.password;

    if (!this.emailInvalid && !this.passwordInvalid) {
      this.credentialsInvalid = false;
      this.loading = true;
      this.authService.login(this.email, this.password).subscribe({
        next: (user) => {
          // Sound permission should be handled by modal/banner elsewhere
          // Use the user object directly for role-based redirects
          if (user) {
            if (user.roles && user.roles.includes('am')) {
              this.router.navigate(['/am-dashboard']);
              setTimeout(() => this.webSocketService.connect(), 500);
            } else if (user.roles && user.roles.includes('tech')) {
              this.router.navigate(['/mobile/new-task']);
              setTimeout(() => this.webSocketService.connect(), 500);
            } else if (user.roles && user.roles.includes('vendor')) {
              this.router.navigate(['/vdashboard']);
              setTimeout(() => this.webSocketService.connect(), 500);
            } else {
              this.loading = false;
              this.tenantService.getUsersAssignToAccess();
              setTimeout(() => this.webSocketService.connect(), 500);
              this.tenantService.userAssignedTo$.pipe(take(1)).subscribe((data: boolean) => {
                this.tenantService.setAssignedToStatus(data);
              });
              this.router.navigate(['/dashboard']);
            }
            // Request notification permission and retrieve the FCM token
            this.pushNotificationService.requestPermission().subscribe({
              next: () => {
                this.pushNotificationService.listenForMessages();
              },
              error: (err) => {
                console.error('Failed to get notification permission:', err);
              }
            });
            // After navigation, check sound permission
            setTimeout(() => {
              if (this.soundService.currentPermission === null) {
                this.dialog.open(SoundPermissionModalComponent, {
                  width: '400px',
                  disableClose: true
                });
              }
            }, 500); // Delay to ensure navigation is complete
          }
        },
        error: (err) => {
          this.loading = false;
          this.credentialsInvalid = true;
          console.error(err);
        }
      });
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  clearEmailError(): void {
    this.emailInvalid = false;
    this.credentialsInvalid = false;
  }

  clearPasswordError(): void {
    this.passwordInvalid = false;
    this.credentialsInvalid = false;
  }

  playPronunciation(): void {
    this.soundService.playSound('alert').catch(error => {
      console.error('Playback failed:', error);
    });
  }
}

